import {
  feedScrape,
  formatRoute,
  SEARCH_FIRST_PAGE,
  type ParsedList,
  type Result,
  type VideoSummary,
} from '@visious/core';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import { text } from '../../strings';
import type { ApiProblem } from '../../api/videos';
import { toListingItems, type ListingItem, type ProgressLookup } from '../../listing/model';

/**
 * §8's `/hashtag/TAG` page, as reasoning. R13.
 *
 * It is **not** a search, which is why it is its own screen rather than a `type` on the other one:
 * `/api/v1/hashtag/{tag}` is a separate endpoint with a different envelope (`{results:[…]}`), a
 * different page size (36 rows, not 20), no filters at all, and a single record kind. Folding it into
 * the search screen would mean four filter controls that silently do nothing — the exact failure §8's
 * suppression rule exists to prevent, self-inflicted.
 *
 * What it *does* share is the listing grid and the problem taxonomy, which is the sharing that matters.
 */

/** 320 — the same number and the same reason as a feed card (`screens/feed/model.ts`). */
const CARD_THUMBNAIL_MIN_WIDTH = 320;
const SKELETON_CARDS = 12;

export type HashtagState =
  | { readonly kind: 'loading'; readonly skeletonCards: number }
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  /**
   * No rows. On page 1 this is the *only* answer an unknown tag gets — the endpoint replies
   * `200 {"results":[]}` for `zzqqxxnope1234` exactly as it does for a tag that has simply run out, so
   * "no such hashtag" and "nothing here" are indistinguishable on the wire and the copy must not claim
   * to know which.
   */
  | { readonly kind: 'empty'; readonly title: string; readonly detail: string; readonly page: number; readonly previousHref: string | null }
  | {
      readonly kind: 'ready';
      readonly items: readonly ListingItem[];
      /** Whether another page may exist — `false` only on the proof of an empty next page. */
      readonly more: boolean;
    };

export interface HashtagStateOptions {
  readonly nowEpochSec: number;
  /** 4b's stored playheads (`useWatchProgressLookup`). */
  readonly progress: ProgressLookup;
}

/**
 * The listing, folded from every page fetched so far — search's shape (`screens/search/model.ts`),
 * one kind of row. Pages overlap here too (`page=2` re-served 2 of page 1's 36 rows), so repeats are
 * folded out by video id. Unlike search, deep paging does not 400: the only end-of-list proof this
 * endpoint gives is a page with nothing on it.
 */
export function hashtagStateFor(
  tag: string,
  page: number,
  results: readonly Result<ParsedList<VideoSummary>, ApiProblem>[] | null,
  options: HashtagStateOptions,
): HashtagState {
  if (results === null || results.length === 0) {
    return { kind: 'loading', skeletonCards: SKELETON_CARDS };
  }

  const items: ListingItem[] = [];
  const seen = new Set<string>();
  let end = false;
  let firstDropped = 0;
  let firstItems = 0;

  for (const [index, result] of results.entries()) {
    if (!result.ok) {
      // A failed page is the page's problem, rows on screen or not — the alternative is a silent
      // Load more that does nothing (`useSubscriptions.ts` makes the same call).
      return { kind: 'problem', copy: describeProblem(result.error, problemSubjectFor(tag)) };
    }
    const { items: videos, dropped } = result.value;
    if (index === 0) {
      firstItems = videos.length;
      firstDropped = dropped.length;
    }
    if (videos.length === 0) end = true;

    const scrape = feedScrape(videos, options.nowEpochSec);
    const rows = toListingItems(videos, {
      nowEpochSec: options.nowEpochSec,
      thumbnailMinWidth: CARD_THUMBNAIL_MIN_WIDTH,
      // Back-computed, like search's: 36 rows shared **2** distinct seconds-of-day (API reference §2).
      absoluteDates: false,
      scrape,
      progress: options.progress,
    });
    for (const row of rows) {
      if (seen.has(row.videoId)) continue;
      seen.add(row.videoId);
      items.push(row);
    }
  }

  if (items.length === 0) {
    if (firstDropped > 0 && firstItems === 0) {
      return {
        kind: 'problem',
        copy: {
          title: text().hashtag.allDropped.title,
          detail: text().hashtag.allDropped.detail,
          retryable: true,
        },
      };
    }
    const words = text().hashtag.empty;
    const exhausted = page > SEARCH_FIRST_PAGE;
    return {
      kind: 'empty',
      title: exhausted ? words.lastPageTitle : words.noneTitle(tag),
      detail: exhausted ? words.lastPageDetail : words.noneDetail,
      page,
      previousHref:
        page <= SEARCH_FIRST_PAGE ? null : formatRoute({ kind: 'hashtag', tag, page: page - 1 }),
    };
  }

  return { kind: 'ready', items, more: !end };
}

/*
 * The two notices this page used to carry — "36 rows carrying titles only" and "2 rows could not be
 * read" — were both about the response rather than about the videos, so they are gone. `feedScrape`
 * still runs: it decides which fields a card *shows*, which is the part that was ever the reader's.
 */

function problemSubjectFor(tag: string) {
  const words = text().hashtag.problem;
  return {
    notFound: words.notFound,
    unreadable: (): ProblemCopy => ({ ...words.unreadable, title: words.unreadableTitle(tag) }),
  };
}
